import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';
import { useState } from 'react';

export default function WeBuyHousesWithTenantsPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    tenantSituation: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, String(value));
      });

      const response = await fetch(
        'https://readdy.ai/api/form/d67a79fmvg9ih2c792s0',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formBody.toString(),
        }
      );

      if (!response.ok) {
        // Throw an error to be caught by the catch block
        const errorText = await response.text();
        throw new Error(`Server responded with ${response.status}: ${errorText}`);
      }

      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        tenantSituation: '',
        message: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
      // Optionally, you could set an error state to display a user‑friendly message
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="We Buy Houses with Tenants El Paso | Even Non-Paying Tenants | TREX RE LLC"
        description="Sell your rental property with tenants in El Paso TX. We buy houses with non-paying tenants, problem tenants, or long-term renters. No eviction needed. Get a fair cash offer today."
        keywords="sell house with tenants El Paso, buy houses with non-paying tenants, sell rental property with problem tenants, landlord exit strategy Texas, cash buyers tenant occupied homes"
        canonical="/elpaso-tx/we-buy-houses-with-tenants"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'We Buy Houses with Tenants in El Paso - Even If They Are Not Paying Rent',
          description:
            'Comprehensive guide for landlords looking to sell rental properties with tenants, including non-paying tenants, in El Paso Texas.',
          author: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
          publisher: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/613f0affb96e3aa38f4e59c9a0cf3227.jpg"
            alt="Landlord with tenant-occupied property"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-6">
              Tenant-Occupied Properties
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              We Buy Houses with Tenants —{' '}
              <span className="text-teal-400">
                Even If They Are Not Paying Rent
              </span>
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Tired of chasing rent payments? Dealing with problem tenants? We
              purchase rental properties in any condition with any tenant
              situation. No eviction required. Close on your timeline.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i> Call (915) 206-5814
              </a>
              <a
                href="#contact-form"
                className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Get Cash Offer
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Get Your Free Cash Offer
            </h3>
            <p className="text-gray-600 mb-6">
              No obligation. No eviction needed.
            </p>
            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-teal-600"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Thank You!
                </h4>
                <p className="text-gray-600">
                  We will contact you within 24 hours with your cash offer.
                </p>
              </div>
            ) : (
              <form
                id="hero-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  required
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  required
                />
                <input
                  type="text"
                  name="address"
                  placeholder="Property Address"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  required
                />
                <select
                  name="tenantSituation"
                  value={formData.tenantSituation}
                  onChange={(e) =>
                    setFormData({ ...formData, tenantSituation: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  required
                >
                  <option value="">Select Tenant Situation</option>
                  <option value="non-paying">Non-Paying Tenant</option>
                  <option value="problem-tenant">Problem Tenant</option>
                  <option value="good-tenant">Good Tenant (Paying)</option>
                  <option value="multiple-tenants">Multiple Tenants</option>
                  <option value="vacant">Currently Vacant</option>
                  <option value="other">Other Situation</option>
                </select>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Get My Cash Offer'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            The Reality of Being a Landlord with Non-Paying Tenants
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <p>
              If you are reading this, chances are you are exhausted. You
              became a landlord hoping for passive income and financial
              security, but instead, you have found yourself trapped in a
              nightmare of missed rent payments, ignored phone calls, and
              mounting frustration. You are not alone. Thousands of property
              owners across El Paso and Texas face the same situation every
              year, and the stress can be overwhelming.
            </p>
            <p>
              The traditional advice is simple: evict the tenant and start
              fresh. But anyone who has been through the eviction process in
              Texas knows it is anything but simple. Between court filings,
              waiting periods, potential appeals, and the very real
              possibility of property damage, eviction can take months and
              cost thousands of dollars — money you may not have, especially
              when rent is not coming in.
            </p>
            <p>
              That is where we come in. At TREX RE LLC, we specialize in
              purchasing properties with tenants still in place, including
              properties with non‑paying tenants, problem tenants, or tenants
              facing their own difficult circumstances. We handle the tenant
              situation so you do not have to. You get cash for your property,
              and you walk away free from the burden that has been weighing
              you down.
            </p>
            <p>
              This comprehensive guide will walk you through everything you
              need to know about selling a house with tenants, your legal
              rights as a landlord in Texas, and how working with a cash
              buyer like us can provide the fastest, most stress‑free solution
              to your tenant troubles.
            </p>
          </div>
        </div>
      </section>

      {/* Why Landlords Struggle Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Why So Many Landlords Are Struggling Right Now
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                <p>
                  The rental market has changed dramatically in recent years.
                  Economic uncertainty, job losses, and rising costs of living
                  have made it increasingly difficult for many tenants to keep
                  up with rent payments. At the same time, landlords face their
                  own financial pressures: mortgage payments, property taxes,
                  insurance, and maintenance costs do not stop just because
                  rent is not coming in.
                </p>
                <p>
                  In El Paso specifically, the rental market presents unique
                  challenges. With a significant military population due to
                  Fort Bliss, landlords often deal with frequent tenant turnover
                  and the complications that come with{' '}
                  <a
                    href="/elpaso-tx/fort-bliss-military-relocation"
                    className="text-teal-600 hover:text-teal-700 font-semibold"
                  >
                    military relocations and PCS moves
                  </a>
                  . Additionally, the border economy creates fluctuations in
                  employment that can affect tenants ability to pay consistently.
                </p>
                <p>
                  Many landlords we work with have tried everything: payment
                  plans, partial rent acceptance, even helping tenants find
                  assistance programs. But sometimes, despite best efforts,
                  the situation simply does not improve. When you have been
                  carrying a non‑paying tenant for months, the financial and
                  emotional toll becomes unsustainable.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=worried%20landlord%20reviewing%20overdue%20rent%20payment%20notices%20and%20bills%20at%20kitchen%20table%20with%20calculator%20showing%20financial%20stress%20from%20rental%20property%20investment%20in%20modern%20home%20setting%20warm%20natural%20lighting%20professional%20photography&width=800&height=600&seq=tenant-struggle-002&orientation=landscape"
                alt="Landlord reviewing overdue rent notices"
                className="w-full h-[400px] object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Common Tenant Situations Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Common Tenant Situations We Handle
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              No matter what tenant challenge you are facing, we have
              experience purchasing properties in similar situations
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Non-Paying Tenants
              </h3>
              <p className="text-gray-600">
                Tenants who have stopped paying rent entirely, whether due to
                job loss, financial hardship, or simply choosing not to pay.
                We purchase these properties without requiring you to go
                through eviction first.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-alert-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Problem Tenants
              </h3>
              <p className="text-gray-600">
                Tenants causing property damage, disturbing neighbors, violating
                lease terms, or creating legal liability. We take on the risk
                and responsibility of dealing with difficult tenant situations.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-yellow-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-time-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Holdover Tenants
              </h3>
              <p className="text-gray-600">
                Tenants whose lease has expired but refuse to leave. These
                situations can be particularly frustrating and legally complex.
                We handle the transition so you do not have to.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-government-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Section 8 Tenants
              </h3>
              <p className="text-gray-600">
                Properties with Housing Choice Voucher tenants. Whether you want
                to exit the Section 8 program or have issues with voucher
                payments, we can purchase your property with tenants in place.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-group-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Family Members as Tenants
              </h3>
              <p className="text-gray-600">
                When family members are living in your property, eviction
                becomes emotionally complicated. We provide a solution that
                allows you to sell without the family conflict of eviction.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-building-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Multi-Unit Properties
              </h3>
              <p className="text-gray-600">
                Duplexes, triplexes, or small apartment buildings with multiple
                tenants in various situations. We can purchase the entire
                property regardless of how many units are occupied.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Texas Landlord Rights Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=texas%20state%20flag%20with%20legal%20documents%20and%20property%20deed%20papers%20on%20professional%20desk%20representing%20landlord%20tenant%20law%20and%20property%20rights%20warm%20office%20lighting%20professional%20legal%20photography&width=800&height=600&seq=tenant-rights-003&orientation=landscape"
                alt="Texas landlord legal rights"
                className="w-full h-[400px] object-cover object-top rounded-xl shadow-lg"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Understanding Your Rights as a Texas Landlord
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                <p>
                  Before exploring your options for selling, it is important
                  to understand your legal rights as a landlord in Texas. The
                  Texas Property Code governs landlord‑tenant relationships
                  and provides specific procedures for handling non‑paying
                  tenants.
                </p>
                <p>
                  <strong>Notice Requirements:</strong> In Texas, if a tenant
                  fails to pay rent, you must provide written notice before
                  filing for eviction. For month‑to‑month tenancies, you must
                  give at least one month notice. For leases with specific
                  terms, the notice period depends on the lease agreement.
                </p>
                <p>
                  <strong>Eviction Process:</strong> The formal eviction
                  process in Texas involves filing a lawsuit in Justice Court,
                  serving the tenant with a citation, attending a court hearing,
                  obtaining a judgment, and then waiting for a constable to
                  physically remove the tenant if they do not leave voluntarily.
                  This entire process typically takes 3‑6 weeks minimum, but
                  can stretch to several months if the tenant contests the
                  eviction or files for bankruptcy.
                </p>
                <p>
                  <strong>Self‑Help Eviction is Illegal:</strong> Texas law
                  strictly prohibits landlords from taking matters into their
                  own hands. You cannot change locks, remove tenant belongings,
                  shut off utilities, or use intimidation to force a tenant out.
                  Doing so can result in significant legal liability and
                  potential lawsuits against you.
                </p>
                <p>
                  <strong>Selling with Tenants:</strong> Here is the good news:
                  you have the legal right to sell your property even with
                  tenants in place. The lease typically transfers to the new
                  owner, but when you sell to a cash buyer like us, we take on
                  all tenant responsibilities and handle any necessary
                  transitions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* True Cost of Eviction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            The True Cost of Eviction: Time, Money, and Stress
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6 mb-12">
            <p>
              Many landlords underestimate the true cost of eviction. It is not
              just about court filing fees. Let us break down what eviction
              really costs:
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Financial Costs
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-gray-200 pb-4">
                <span className="text-gray-700 font-semibold">
                  Court filing fees
                </span>
                <span className="text-gray-900 font-bold">$100 - $150</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-4">
                <span className="text-gray-700 font-semibold">
                  Service of citation
                </span>
                <span className="text-gray-900 font-bold">$75 - $125</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-4">
                <span className="text-gray-700 font-semibold">
                  Attorney fees (if contested)
                </span>
                <span className="text-gray-900 font-bold">$1,500 - $5,000</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-4">
                <span className="text-gray-700 font-semibold">
                  Lost rent during process (3‑6 months)
                </span>
                <span className="text-gray-900 font-bold">$3,000 - $9,000</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-4">
                <span className="text-gray-700 font-semibold">
                  Property damage repairs
                </span>
                <span className="text-gray-900 font-bold">$2,000 - $15,000</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-4">
                <span className="text-gray-700 font-semibold">
                  Cleaning and turnover costs
                </span>
                <span className="text-gray-900 font-bold">$500 - $2,000</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-4">
                <span className="text-gray-700 font-semibold">
                  Marketing for new tenant
                </span>
                <span className="text-gray-900 font-bold">$200 - $500</span>
              </div>
              <div className="flex justify-between items-center pt-4">
                <span className="text-xl text-gray-900 font-bold">
                  Total Estimated Cost
                </span>
                <span className="text-2xl text-red-600 font-bold">
                  $7,375 - $31,775
                </span>
              </div>
            </div>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <p>
              <strong>Time Costs:</strong> Beyond money, eviction consumes
              enormous amounts of your time. Court appearances, paperwork,
              coordinating with attorneys, dealing with property damage,
              finding new tenants — all while managing the emotional stress
              of the situation. For many landlords, this time could be better
              spent on their career, family, or other investments.
            </p>
            <p>
              <strong>Emotional and Health Costs:</strong> The stress of
              dealing with problem tenants takes a real toll on your mental
              and physical health. Sleep loss, anxiety, frustration, and the
              constant worry about what might happen next can affect your
              overall quality of life. Many landlords we work with describe
              feeling trapped and overwhelmed by their tenant situation.
            </p>
            <p>
              <strong>Opportunity Costs:</strong> While you are tied up with
              eviction proceedings and property issues, you are missing out
              on other opportunities. That capital tied up in a non‑performing
              rental property could be invested elsewhere, generating actual
              returns instead of losses.
            </p>
          </div>
        </div>
      </section>

      {/* Why Cash Buyers Can Purchase Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Why Cash Buyers Can Purchase Homes That Need Repairs
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                <p>
                  You might wonder how we can purchase properties with
                  non‑paying or problem tenants when traditional buyers cannot.
                  The answer lies in our business model and experience.
                </p>
                <p>
                  <strong>We Are Investors, Not Homeowners:</strong> Unlike
                  traditional buyers who need a move‑in ready home, we are
                  professional real estate investors. We have systems in place
                  for handling tenant situations, whether that means working
                  with existing tenants, facilitating their relocation, or
                  going through the eviction process ourselves if necessary.
                </p>
                <p>
                  <strong>We Have the Resources:</strong> We have the financial
                  resources to handle tenant‑related costs and the legal
                  expertise to navigate complex situations. We also have
                  relationships with property managers, attorneys, and other
                  professionals who help us resolve tenant issues efficiently.
                </p>
                <p>
                  <strong>We Buy for Long‑Term Investment:</strong> We are not
                  looking for a quick flip. We often hold properties long‑term
                  as rentals or make strategic improvements before reselling.
                  This long‑term perspective allows us to take on properties
                  that other buyers would avoid.
                </p>
                <p>
                  <strong>We Understand the Numbers:</strong> We know how to
                  accurately assess the value of a property with tenant issues
                  and make offers that work for both parties. We factor in all
                  the costs and risks, but we also see the potential that
                  others might miss.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20cash%20home%20buyer%20shaking%20hands%20with%20relieved%20landlord%20outside%20rental%20property%20successful%20real%20estate%20transaction%20warm%20afternoon%20lighting%20showing%20trust%20and%20satisfaction%20professional%20photography&width=800&height=600&seq=tenant-cashbuyer-004&orientation=landscape"
                alt="Cash buyer helping landlord"
                className="w-full h-[400px] object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Simple 4‑Step Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Selling your tenant‑occupied property to us is straightforward
              and stress‑free
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-teal-600">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Contact Us
              </h3>
              <p className="text-gray-600">
                Call us at (915) 206‑5814 or fill out our form. Tell us about
                your property and tenant situation. We listen without judgment
                and keep everything confidential.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-teal-600">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Property Evaluation
              </h3>
              <p className="text-gray-600">
                We evaluate your property and tenant situation. In many cases,
                we can make an offer without even entering the property if
                tenant access is an issue.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-teal-600">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Receive Cash Offer
              </h3>
              <p className="text-gray-600">
                We present you with a fair, no‑obligation cash offer. We
                explain exactly how we arrived at the number and answer all
                your questions.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-teal-600">4</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Close on Your Timeline
              </h3>
              <p className="text-gray-600">
                Choose your closing date. We can close in as little as 7 days
                or wait until a time that works better for you. You get cash
                and walk away free from tenant headaches.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Comparing Your Options
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl shadow-lg">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">
                    Factor
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-teal-600">
                    Sell to Us
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                    Evict Then Sell
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                    List with Agent
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-700 font-semibold">
                    Timeline
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    7‑14 days
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    4‑9 months
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    6‑12 months
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-700 font-semibold">
                    Eviction Required
                  </td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-close-circle-line text-2xl text-teal-600"></i>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-check-line text-2xl text-red-600"></i>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-check-line text-2xl text-red-600"></i>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-700 font-semibold">
                    Repairs Needed
                  </td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-close-circle-line text-2xl text-teal-600"></i>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-check-line text-2xl text-red-600"></i>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-check-line text-2xl text-red-600"></i>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-700 font-semibold">
                    Commission Fees
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    $0
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    $0
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    5‑6%
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-700 font-semibold">
                    Closing Costs
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    We pay
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    You pay
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    You pay
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-700 font-semibold">
                    Stress Level
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-teal-600 font-bold">
                    Minimal
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-red-600 font-bold">
                    Very High
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-orange-600 font-bold">
                    High
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-700 font-semibold">
                    Guaranteed Sale
                  </td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-check-line text-2xl text-teal-600"></i>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-close-circle-line text-2xl text-red-600"></i>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-close-circle-line text-2xl text-red-600"></i>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Real Scenarios Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Real Scenarios: Landlords We Have Helped
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These are real situations from landlords we have worked with
              (names changed for privacy)
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-user-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Maria's Story: The Inherited Rental
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    El Paso, TX • Single‑Family Home
                  </p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                Maria inherited a rental property from her father. The tenant
                had been there for years but stopped paying rent after losing
                his job. Maria lived out of state and could not manage the
                eviction process from afar. She also faced{' '}
                <a
                  href="/elpaso-tx/cant-afford-repairs-to-sell-house"
                  className="text-teal-600 hover:text-teal-700 font-semibold"
                >
                  cannot afford the repairs needed to sell traditionally
                </a>
                .
              </p>
              <p className="text-gray-700">
                <strong>Solution:</strong> We purchased the property with the
                tenant in place, handled all the legal aspects, and closed in
                10 days. Maria received cash and was able to settle her
                father's estate without the stress of long‑distance landlording.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-user-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    James's Story: The Problem Tenant
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    El Paso, TX • Duplex
                  </p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                James had a tenant who was causing significant property damage
                and disturbing neighbors. The tenant was paying rent
                sporadically but the stress and liability were overwhelming.
                James worried about potential lawsuits from neighbors and the
                cost of repairs.
              </p>
              <p className="text-gray-700">
                <strong>Solution:</strong> We purchased the duplex and took
                over all tenant responsibilities. James walked away with cash
                and peace of mind, no longer worried about late‑night calls
                from angry neighbors or mounting repair costs.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-user-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Robert's Story: Facing Foreclosure
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    El Paso, TX • Single‑Family Home
                  </p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                Robert's tenant stopped paying rent, but Robert still had to
                make mortgage payments. After six months of no income, he was
                facing{' '}
                <a
                  href="/avoid-foreclosure"
                  className="text-teal-600 hover:text-teal-700 font-semibold"
                >
                  potential foreclosure
                </a>{' '}
                on the rental property. He could not afford to wait months for
                eviction and then more months to sell traditionally.
              </p>
              <p className="text-gray-700">
                <strong>Solution:</strong> We made a cash offer that paid off
                Robert's mortgage and gave him enough to avoid foreclosure. We
                closed in 8 days, stopping the foreclosure process and saving
                his credit.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-user-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Linda's Story: Family Member Tenant
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    El Paso, TX • Townhouse
                  </p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                Linda's adult son was living in her rental property but had
                stopped paying rent. She could not bring herself to evict her
                own child, but she needed the money from the property to fund
                her retirement. The emotional toll was affecting her health
                and family relationships.
              </p>
              <p className="text-gray-700">
                <strong>Solution:</strong> We purchased the property and worked
                with Linda's son to find alternative housing. This allowed
                Linda to get the cash she needed without the emotional trauma
                of evicting her own child.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Happens to Tenants Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20homeowners%20and%20landlords%20in%20different%20life%20situations%20showing%20relief%20and%20hope%20professional%20real%20estate%20consultation%20setting%20warm%20natural%20lighting%20representing%20various%20tenant%20challenges%20professional%20photography&width=800&height=600&seq=tenant-situations-005&orientation=landscape"
                alt="Diverse landlord situations"
                className="w-full h-[400px] object-cover object-top rounded-xl shadow-lg"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                What Happens to the Tenants After We Buy?
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                <p>
                  This is a common question, and it is one we take seriously.
                  We handle tenant situations professionally and ethically.
                </p>
                <p>
                  <strong>For Non‑Paying Tenants:</strong> We have the
                  resources and experience to handle the eviction process if
                  necessary. However, we often work with tenants to find
                  solutions that work for everyone, including cash‑for‑keys
                  agreements where we pay tenants to vacate voluntarily.
                </p>
                <p>
                  <strong>For Paying Tenants:</strong> If the tenant is
                  current on rent and wants to stay, we may honor the existing
                  lease and become their new landlord. This provides continuity
                  for good tenants while giving you the exit you need.
                </p>
                <p>
                  <strong>For Problem Tenants:</strong> We have systems in
                  place to handle difficult tenant situations safely and
                  legally. We work with attorneys and property managers who
                  specialize in these scenarios.
                </p>
                <p>
                  The key point is this: once we purchase your property, the
                  tenant situation becomes our responsibility, not yours. You
                  are free from the stress, liability, and financial burden.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Considerations Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Legal Considerations When Selling with Tenants
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <p>
              When selling a property with tenants, there are important legal
              considerations to keep in mind:
            </p>
            <p>
              <strong>Lease Transfer:</strong> In most cases, when you sell a
              property, the existing lease transfers to the new owner. This
              means the tenant's rights under the lease continue. However, when
              you sell to us, we take on all lease obligations and tenant
              responsibilities.
            </p>
            <p>
              <strong>Security Deposits:</strong> Texas law requires that
              security deposits be transferred to the new owner or returned to
              the tenant. We handle all security deposit transfers as part of
              the closing process, ensuring full legal compliance.
            </p>
            <p>
              <strong>Notice to Tenants:</strong> Tenants must be notified of
              the property sale and provided with the new owner's contact
              information. We manage all required notifications and ensure
              proper documentation.
            </p>
            <p>
              <strong>Disclosure Requirements:</strong> You are required to
              disclose the tenant situation to buyers. When you work with us,
              we want full transparency about the tenant situation. This
              protects you legally and allows us to make the most accurate
              offer.
            </p>
            <p>
              <strong>Lease Review:</strong> We review existing leases as part
              of our evaluation process. If there are{' '}
              <a
                href="/title-issues"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                title complications or liens
              </a>{' '}
              related to the property, we can often help resolve those as well.
            </p>
          </div>
        </div>
      </section>

      {/* Tips for Maximizing Offer Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Tips for Maximizing Your Cash Offer
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-file-list-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Provide Complete Documentation
              </h3>
              <p className="text-gray-600">
                Have copies of the lease, rent payment history, and any
                correspondence with tenants ready. Complete documentation helps
                us make the most accurate offer.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-chat-check-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Be Honest About the Situation
              </h3>
              <p className="text-gray-600">
                Full transparency about tenant issues helps us provide the best
                solution. We have seen it all and will not judge your situation.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-time-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Act Quickly
              </h3>
              <p className="text-gray-600">
                The longer you wait, the more money you lose in unpaid rent and
                potential damage. Reaching out sooner rather than later can
                save you thousands.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Maintain Property Access
              </h3>
              <p className="text-gray-600">
                If possible, maintain your legal right to access the property
                for inspections. This helps us evaluate the property more
                accurately.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-folder-info-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Gather Property Information
              </h3>
              <p className="text-gray-600">
                Information about recent repairs, property tax status, and any
                liens helps us process your offer faster and more accurately.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-question-answer-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Ask Questions
              </h3>
              <p className="text-gray-600">
                Do not hesitate to ask us questions about the process, timeline,
                or offer. We want you to feel completely comfortable with your
                decision.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Can I really sell my house with non‑paying tenants?
              </h3>
              <p className="text-gray-700">
                Yes, absolutely. We specialize in purchasing properties with
                tenant issues, including non‑paying tenants. You do not need
                to evict them first. We handle the entire tenant situation
                after purchase.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Will I get less money because there are tenants?
              </h3>
              <p className="text-gray-700">
                Our offer factors in the tenant situation and any associated
                costs. While the offer may be adjusted for tenant‑related
                risks, you save all the money you would have spent on eviction,
                repairs, carrying costs, and agent commissions. Many sellers
                find they net more by selling to us than going through
                traditional eviction and sale.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                What if the tenant damages the property before closing?
              </h3>
              <p className="text-gray-700">
                We account for potential damage in our offer. Once we have an
                agreement, the property becomes our concern. We have insurance
                and resources to handle any damage that occurs.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Do I need to tell the tenant I am selling?
              </h3>
              <p className="text-gray-700">
                Texas law requires notification to tenants about the sale. We
                can guide you through this process and handle communications
                with tenants if you prefer. We have experience managing these
                conversations professionally.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                What if I have multiple properties with tenant problems?
              </h3>
              <p className="text-gray-700">
                We can purchase multiple properties at once. Many investors
                who are exiting the rental business sell us their entire
                portfolio. This can be an efficient way to liquidate multiple
                problem properties simultaneously.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                How quickly can we close?
              </h3>
              <p className="text-gray-700">
                We can close in as little as 7 days if needed. However, we are
                flexible and can work with your timeline. If you need more time
                to make arrangements, we can accommodate that as well.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                What if the tenant has a lease that is not expired?
              </h3>
              <p className="text-gray-700">
                We can purchase properties with active leases. The lease
                typically transfers to us as the new owner. We review the lease
                terms as part of our evaluation and factor them into our offer.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Are there any fees or commissions?
              </h3>
              <p className="text-gray-700">
                No. We do not charge any fees or commissions. We also typically
                cover closing costs. The offer we present is what you will
                receive at closing (minus any liens or mortgages that need to
                be paid off).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Middle CTA Section */}
      <section id="contact-form" className="py-20 bg-teal-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Sell Your Tenant-Occupied Property?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Get a fair cash offer with no obligation. We handle the tenant
            situation so you do not have to.
          </p>
          <div className="bg-white rounded-xl p-8 shadow-2xl max-w-2xl mx-auto">
            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-teal-600"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Thank You!
                </h4>
                <p className="text-gray-600">
                  We will contact you within 24 hours with your cash offer.
                </p>
              </div>
            ) : (
              <form
                id="middle-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                  <input
                    type="text"
                    name="address"
                    placeholder="Property Address"
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <select
                  name="tenantSituation"
                  value={formData.tenantSituation}
                  onChange={(e) =>
                    setFormData({ ...formData, tenantSituation: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  required
                >
                  <option value="">Select Tenant Situation</option>
                  <option value="non-paying">Non-Paying Tenant</option>
                  <option value="problem-tenant">Problem Tenant</option>
                  <option value="good-tenant">Good Tenant (Paying)</option>
                  <option value="multiple-tenants">Multiple Tenants</option>
                  <option value="vacant">Currently Vacant</option>
                  <option value="other">Other Situation</option>
                </select>
                <textarea
                  name="message"
                  placeholder="Tell us more about your situation (optional)"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      message: e.target.value.slice(0, 500),
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm h-24 resize-none"
                  maxLength={500}
                ></textarea>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Get My Free Cash Offer'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
